import { detectLanguage, languageInfo, normalizePhrase, type Language } from '@elena/shared';
import { api } from './api';
import { voicePerformance, voiceScript } from '@elena/shared';
import { trackVoiceAudio } from './voicePerformance';

let language: Language = 'sr';
/** The conversation language from the server (state.tsx): recognizers listen in it. */
export function setSpeechLanguage(next: Language): void {
  language = next;
}
/** BCP-47 tag for the recognizers, e.g. en-US. */
export const recognitionLang = (): string => languageInfo[language].speech;

/** Minimal typing for the Web Speech API, which TypeScript's DOM lib doesn't fully cover. */
interface RecognitionResult {
  isFinal: boolean;
  /** Alternatives, best first (as many as maxAlternatives asked for). */
  length: number;
  0: { transcript: string };
  [index: number]: { transcript: string } | undefined;
}
interface RecognitionEvent {
  resultIndex: number;
  results: { length: number; [index: number]: RecognitionResult };
}
export interface Recognition {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  maxAlternatives?: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: RecognitionEvent) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}

/** Shared "who is using the mic / speaker" signals, so the wake word listener can step aside. */
type Flag = { on: boolean; listeners: Set<(on: boolean) => void> };
const flag = (): Flag => ({ on: false, listeners: new Set() });
const set = (f: Flag, on: boolean) => {
  if (f.on === on) return;
  f.on = on;
  f.listeners.forEach((l) => l(on));
};
const watch = (f: Flag, l: (on: boolean) => void) => {
  f.listeners.add(l);
  return () => void f.listeners.delete(l);
};
const micBusy = flag();
/** For other recognizers (Azure): while on, "Hej Elena" steps aside. */
export const markMicBusy = (on: boolean) => set(micBusy, on);
const speaking = flag();
watch(speaking, (on) => {
  voicePerformance.setSpeaking(on);
  if (!on) voiceScript.stop();
});

/**
 * The same signals from other Elena windows of this browser (a tab and the agent's widget).
 * Chrome runs one speech recognition at a time, so a wake listener restarting in one window
 * would cut off the mic button in another.
 */
const remoteMic = flag();
const remoteSpeaking = flag();
// A window that closes mid-sentence never says it is done: forget its flag after a while.
const REMOTE_TTL_MS = 30_000;
const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('elena-mic');
const remoteTimers = new Map<Flag, number>();
const setRemote = (f: Flag, on: boolean) => {
  window.clearTimeout(remoteTimers.get(f));
  if (on)
    remoteTimers.set(
      f,
      window.setTimeout(() => set(f, false), REMOTE_TTL_MS),
    );
  set(f, on);
};
if (channel) {
  channel.onmessage = (e: MessageEvent<{ mic?: boolean; speaking?: boolean; stop?: boolean }>) => {
    if (typeof e.data?.mic === 'boolean') setRemote(remoteMic, e.data.mic);
    if (typeof e.data?.speaking === 'boolean') setRemote(remoteSpeaking, e.data.speaking);
    // "Hej Elena" in another window: be quiet here too
    if (e.data?.stop) stopSpeaking();
  };
  watch(micBusy, (on) => channel.postMessage({ mic: on }));
  watch(speaking, (on) => channel.postMessage({ speaking: on }));
  window.addEventListener('pagehide', () => {
    if (micBusy.on) channel.postMessage({ mic: false });
    if (speaking.on) channel.postMessage({ speaking: false });
  });
}

const either = (a: Flag, b: Flag, l: (on: boolean) => void) => {
  const fire = () => l(a.on || b.on);
  const unA = watch(a, fire);
  const unB = watch(b, fire);
  return () => {
    unA();
    unB();
  };
};
/** True while the mic button listens here or in another Elena window. */
export const onMicBusy = (l: (on: boolean) => void) => either(micBusy, remoteMic, l);
export const onSpeaking = (l: (on: boolean) => void) => either(speaking, remoteSpeaking, l);
export const isMicBusy = () => micBusy.on || remoteMic.on;
export const isSpeaking = () => speaking.on || remoteSpeaking.on;

export const RecognitionCtor = (): (new () => Recognition) | undefined => {
  const w = window as unknown as {
    SpeechRecognition?: new () => Recognition;
    webkitSpeechRecognition?: new () => Recognition;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
};

export const canListen = (): boolean => Boolean(RecognitionCtor());

export interface ListenHandlers {
  onInterim: (text: string) => void;
  /** alternatives: the recognizer's other guesses for the whole message (best first). */
  onFinal: (text: string, alternatives: string[]) => void;
  onError: (message: string) => void;
  onEnd: () => void;
  /** Words already heard (after "Hej Elena, ..."): the message goes on from there. */
  seed?: string;
}

const errorMessages: Record<string, string> = {
  'not-allowed': 'Dozvoli mikrofon u browseru da bih te čula.',
  'no-speech': 'Nisam čula ništa. Probaj ponovo.',
  network: 'Prepoznavanje govora traži internet.',
  'audio-capture': 'Ne vidim mikrofon na ovom uređaju.',
};

/**
 * The whole message as each of the recognizer's other guesses would have it: guess k of every
 * finished phrase (or its best one when it has fewer), after the words from "Hej Elena".
 */
export function alternativesOf(
  seed: string,
  phrases: string[][],
  interim: string,
  text: string,
): string[] {
  const most = Math.max(0, ...phrases.map((p) => p.length));
  const out: string[] = [];
  for (let k = 1; k < most; k++) {
    const words = phrases.map((p) => p[k] ?? p[0] ?? '').join(' ');
    const whole = [seed, stripAck([words, interim].filter(Boolean).join(' '))]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (whole && whole !== text && !out.includes(whole)) out.push(whole);
  }
  return out;
}

/** How long a pause means "I'm done": short breaths mid-sentence must not cut you off. */
export const SILENCE_MS = 1800;
/** Nothing at all said for this long: stop listening. */
export const NOTHING_SAID_MS = 8000;
/** A safety cap for one message. */
export const MAX_LISTEN_MS = 60_000;

/**
 * Starts Serbian speech recognition (free, runs in the browser) and keeps listening through
 * pauses: the message is sent once you have been quiet for SILENCE_MS. Returns a stop function
 * (stopping early still sends what was heard).
 */
export function listen(handlers: ListenHandlers): () => void {
  const Ctor = RecognitionCtor();
  if (!Ctor) {
    handlers.onError('Ovaj browser ne podržava glas. Koristi Chrome ili Edge.');
    handlers.onEnd();
    return () => undefined;
  }
  const rec = new Ctor();
  rec.lang = recognitionLang();
  rec.interimResults = true;
  // one sentence mode ends on the first breath; continuous waits for the silence timer below
  rec.continuous = true;
  // other guesses go to Elena, who picks the one that makes sense (a misheard name)
  rec.maxAlternatives = 3;
  /** per finished phrase: its guesses, best first */
  let finalGuesses: string[][] = [];
  const seed = handlers.seed?.trim() ?? '';
  let finalText = '';
  let interim = '';
  let stopped = false;
  let silence: number | undefined;
  // what the mic caught after the wake words may start with Elena's own "Mhm?": left out
  const heard = () =>
    [seed, stripAck([finalText, interim].filter(Boolean).join(' '))]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

  const stop = () => {
    if (stopped) return;
    stopped = true;
    window.clearTimeout(silence);
    window.clearTimeout(cap);
    try {
      rec.stop();
    } catch {
      // already ended
    }
  };
  const waitForSilence = (ms: number) => {
    window.clearTimeout(silence);
    silence = window.setTimeout(stop, ms);
  };
  const cap = window.setTimeout(stop, MAX_LISTEN_MS);

  rec.onresult = (e) => {
    // rebuild from every result: in continuous mode earlier ones stay in the list
    let done = '';
    let pending = '';
    const guesses: string[][] = [];
    for (let i = 0; i < e.results.length; i++) {
      const r = e.results[i];
      if (!r) continue;
      if (r.isFinal) {
        done += ` ${r[0].transcript}`;
        const g: string[] = [];
        for (let k = 0; k < r.length; k++) g.push(r[k]?.transcript ?? '');
        guesses.push(g);
      } else pending += ` ${r[0].transcript}`;
    }
    finalGuesses = guesses;
    finalText = done.trim();
    interim = pending.trim();
    handlers.onInterim(heard());
    waitForSilence(SILENCE_MS);
  };
  rec.onerror = (e) => {
    if (e.error === 'aborted' || (e.error === 'no-speech' && heard())) return;
    handlers.onError(errorMessages[e.error] ?? 'Glas nije uspeo.');
  };
  rec.onend = () => {
    stopped = true;
    window.clearTimeout(silence);
    window.clearTimeout(cap);
    set(micBusy, false);
    const text = heard();
    if (text) handlers.onFinal(text, alternativesOf(seed, finalGuesses, interim, text));
    handlers.onEnd();
  };
  set(micBusy, true);
  if (seed) handlers.onInterim(seed);
  // after "Hej Elena, pusti..." the sentence may already be over: wait one pause, not the full 8 s
  waitForSilence(seed ? SILENCE_MS : NOTHING_SAID_MS);
  rec.start();
  return stop;
}

let current: HTMLAudioElement | null = null;
let stopTracking: (() => void) | null = null;

/** Speaks with the server voice (Azure, Serbian) and falls back to the browser voice. */
/** What Elena is saying right now, so the wake listener doesn't take her own voice for you. */
let spokenText = '';
let spokenUntil = 0;
watch(speaking, (on) => {
  if (!on) spokenUntil = Date.now() + 2500;
});

/** True when the recognizer only heard Elena herself (part of what she is saying right now). */
export function isOwnVoice(heard: string): boolean {
  // the recognizer sends a phrase ~1 s after it was said: keep checking a bit after she stops
  if (!spokenText || (!isSpeaking() && Date.now() > spokenUntil)) return false;
  const h = normalizePhrase(heard);
  return h.length > 0 && normalizePhrase(spokenText).includes(h);
}

/** Stops Elena talking in this window and every other Elena window of this browser. */
export function stopSpeakingEverywhere(): void {
  stopSpeaking();
  channel?.postMessage({ stop: true });
}

/** Short replies right after "Hej Elena", so you know she heard. Never "Da?": "da li..." starts commands. */
const ackPhrases: Record<Language, string[]> = {
  sr: ['Mhm?', 'Aha?', 'Izvoli.', 'Tu sam.', 'Slušam.'],
  en: ['Mhm?', "I'm listening.", 'Go ahead.'],
  de: ['Mhm?', 'Ich höre.', 'Ja, bitte?'],
};
/** phrase → object URL of the server voice (null = no server voice: the browser one is used) */
const ackAudio = new Map<string, Promise<string | null>>();
let serverAck = true;

function ackUrl(text: string): Promise<string | null> {
  let url = ackAudio.get(text);
  if (!url) {
    url = api
      .tts(text)
      .then((blob) => URL.createObjectURL(blob))
      .catch(() => {
        serverAck = false;
        return null;
      });
    ackAudio.set(text, url);
  }
  return url;
}

/** Loads the acknowledgements ahead of time, so "Mhm?" comes without a delay. */
export function warmAcknowledge(): void {
  if (serverAck) for (const p of ackPhrases[language]) void ackUrl(p);
}

/** A random "Mhm?" / "Izvoli." in the conversation language, right after the wake word. */
export async function acknowledge(): Promise<void> {
  const list = ackPhrases[language];
  const text = list[Math.floor(Math.random() * list.length)] ?? 'Mhm?';
  const url = serverAck
    ? await Promise.race([ackUrl(text), new Promise<null>((r) => setTimeout(() => r(null), 800))])
    : null;
  if (url) {
    await new Audio(url).play().catch(() => undefined);
    return;
  }
  if (!('speechSynthesis' in window)) return;
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = languageInfo[language].speech;
  utterance.rate = 1.05;
  speechSynthesis.speak(utterance);
}

/** The mic may catch the acknowledgement before your words ("Mhm? pusti Cecu"): drop it. */
export function stripAck(text: string): string {
  let t = text.trim();
  for (const phrase of Object.values(ackPhrases).flat()) {
    const words = normalizePhrase(phrase);
    // take spoken tokens until they make up the phrase ("I'm listening." is 2 tokens, 3 words)
    const tokens = t.split(/\s+/).filter(Boolean);
    let used = 0;
    let said = '';
    while (used < tokens.length && said.length < words.length) {
      said = normalizePhrase(tokens.slice(0, ++used).join(' '));
    }
    if (said === words) t = tokens.slice(used).join(' ');
  }
  return t.trim();
}

/** Elena's "Mhm?" heard at the end of a sentence ("pusti Cecu Mhm?"): dropped as well. */
export function stripAckEnd(text: string): string {
  const tokens = text.trim().split(/\s+/).filter(Boolean);
  for (const phrase of Object.values(ackPhrases).flat()) {
    const words = normalizePhrase(phrase);
    for (let used = 1; used <= Math.min(3, tokens.length); used++) {
      if (normalizePhrase(tokens.slice(-used).join(' ')) === words) {
        return tokens.slice(0, -used).join(' ');
      }
    }
  }
  return text.trim();
}

export async function speak(text: string, serverVoice: boolean): Promise<void> {
  stopSpeaking();
  // Every speak() or stopSpeaking() starts a new generation. A voice that arrives from the server
  // after that is stale: playing it would put two Elenas on top of each other.
  const generation = voiceGeneration;
  spokenText = text;
  if (serverVoice) {
    try {
      const blob = await api.tts(text);
      if (generation !== voiceGeneration) return;
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      current = audio;
      const cleanupTracking = await trackVoiceAudio(audio);
      if (generation !== voiceGeneration) {
        cleanupTracking();
        URL.revokeObjectURL(url);
        return;
      }
      stopTracking = cleanupTracking;
      const done = () => {
        URL.revokeObjectURL(url);
        if (current === audio) {
          cleanupTracking();
          stopTracking = null;
          set(speaking, false);
        }
      };
      audio.addEventListener('ended', done, { once: true });
      audio.addEventListener('error', done, { once: true });
      set(speaking, true);
      await audio.play();
      // the 3D avatar times her lips to the real audio length
      if (generation === voiceGeneration) voiceScript.start(text, audio.duration * 1000);
      return;
    } catch {
      // stopped on purpose (play() rejects with AbortError): no browser voice either
      if (generation !== voiceGeneration) return;
      // fall through to the browser voice
      stopTracking?.();
      stopTracking = null;
      set(speaking, false);
    }
  }
  if (!('speechSynthesis' in window)) return;
  const utterance = new SpeechSynthesisUtterance(text);
  // the browser voice of the text's own language (no Serbian voice: Croatian / Bosnian)
  const lang = detectLanguage(text) ?? language;
  utterance.lang = languageInfo[lang].speech;
  const prefixes = lang === 'sr' ? ['sr', 'hr', 'bs'] : [lang];
  const voice = speechSynthesis
    .getVoices()
    .find((v) => prefixes.some((p) => v.lang.toLowerCase().startsWith(p)));
  if (voice) utterance.voice = voice;
  utterance.rate = 0.98;
  utterance.pitch = 1.1;
  utterance.onboundary = () => {
    if (generation === voiceGeneration) voicePerformance.boundary();
  };
  utterance.onstart = () => {
    if (generation === voiceGeneration) voiceScript.start(text);
  };
  utterance.onend = () => {
    if (generation === voiceGeneration) set(speaking, false);
  };
  utterance.onerror = () => {
    if (generation === voiceGeneration) set(speaking, false);
  };
  set(speaking, true);
  speechSynthesis.speak(utterance);
}

let voiceGeneration = 0;

export function stopSpeaking(): void {
  voiceGeneration += 1;
  current?.pause();
  current = null;
  stopTracking?.();
  stopTracking = null;
  if ('speechSynthesis' in window) speechSynthesis.cancel();
  set(speaking, false);
}

/** Speaks and resolves when Elena has finished (or speaking failed), for hands-free turns. */
export function speakFully(text: string, serverVoice: boolean): Promise<void> {
  return new Promise((resolve) => {
    let started = false;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      off();
      window.clearTimeout(guard);
      resolve();
    };
    const off = onSpeaking((on) => {
      if (on) started = true;
      else if (started) finish();
    });
    // Nothing started within a few seconds (no voice at all): carry on.
    const guard = window.setTimeout(() => !started && finish(), 6000);
    void speak(text, serverVoice).then(() => {
      if (!started && !isSpeaking()) finish();
    });
  });
}
